import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  DashboardPeriodKey,
  DashboardRange,
} from '../../utils/dashboardPeriods';
import type { ServiceSalesFigures } from '../../utils/serviceSalesFigures';

export type DashboardMetricKey =
  | 'totalSales'
  | 'totalProfit'
  | 'totalOrders'
  | 'totalMassSold'
  | 'totalExpenses'
  | 'totalQuantitiesSold';

export type DashboardComparisonMetric = {
  change: number;
  percentageChange: number;
};

export type DashboardPreviousPeriod = {
  periodKey: DashboardPeriodKey;
  startDate: string;
  endDate: string;
} & Record<DashboardMetricKey, number>;

/** Service figures; optional because older servers don't send them. */
export type DashboardServiceMetricKey =
  | 'totalServicesSold'
  | 'totalServiceHoursSold';

export type DashboardTotals = Record<DashboardMetricKey, number> &
  ServiceSalesFigures & {
    previousPeriod?: DashboardPreviousPeriod;
    comparison?: Partial<
      Record<
        DashboardMetricKey | DashboardServiceMetricKey,
        DashboardComparisonMetric
      >
    >;
  };

/** ================= Payment Method Breakdown ================= */

export type PaymentMethodRow = {
  method: string;
  gross: number;
  refunded: number;
  net: number;
  count: number;
  refund_count: number;
  share_net: number;
};

export type PaymentMethodTotals = {
  collected_gross: number;
  refunded: number;
  collected_net: number;
  tender_count: number;
};

export type PaymentMethodPreviousPeriod = {
  startDate: string;
  endDate: string;
  totals: PaymentMethodTotals;
  by_method: PaymentMethodRow[];
};

export type PaymentMethodComparisonMetric = {
  change: number;
  percentageChange: number;
};

export type PaymentMethodTotalsComparisonKey =
  | 'collected_net'
  | 'collected_gross'
  | 'tender_count';

export type PaymentMethodMethodComparison = {
  method: string;
  net?: PaymentMethodComparisonMetric;
  gross?: PaymentMethodComparisonMetric;
  count?: PaymentMethodComparisonMetric;
};

export type PaymentMethodComparison = {
  totals: Partial<
    Record<PaymentMethodTotalsComparisonKey, PaymentMethodComparisonMetric>
  >;
  by_method: PaymentMethodMethodComparison[];
};

export type PaymentMethodSeriesPoint = {
  bucket: string;
  label: string;
} & Record<string, string | number>;

/** One tender, as listed under its series bucket (includePayments=true). */
export type PaymentMethodBreakdownPayment = {
  id: string;
  method: string;
  /** Net of the change handed back on this tender, like the series values. */
  amount: number;
  paid_at: string | null;
  receipt: string | null;
};

export type PaymentMethodBreakdown = {
  timezone: string;
  startDate: string;
  endDate: string;
  periodKey: string;
  net: boolean;
  totals: PaymentMethodTotals;
  by_method: PaymentMethodRow[];
  outstanding: {
    amount_due: number;
    orders: number;
    /** Unpaid orders with nothing collected yet. */
    pending?: { amount_due: number; orders: number };
    /** Orders with a part payment already taken. */
    partial?: { amount_due: number; orders: number };
  };
  reconciliation: {
    realisedSales: number;
    collectedNet: number;
    delta: number;
  };
  unattributed_refunds: { amount: number; count: number };
  previousPeriod?: PaymentMethodPreviousPeriod;
  comparison?: PaymentMethodComparison;
  bucket?: string;
  requestedBucket?: string;
  coarsened?: boolean;
  series?: PaymentMethodSeriesPoint[];
  /** Keyed by series `bucket`, newest first. Only with includePayments=true. */
  payments?: Record<string, PaymentMethodBreakdownPayment[]>;
};

export interface DashboardSliceObject {
  dashboardData: DashboardTotals | null;
  loading: boolean;
  error?: string | null;
  selectedRange: DashboardRange;
  /** Today's totals for HomeScreen owner card - separate from BackOffice selected range */
  todayDashboardData: DashboardTotals | null;
  todayDashboardLoading: boolean;
  todayDashboardError: string | null;
  /** Payment method breakdown for the selected dashboard range */
  paymentBreakdown: PaymentMethodBreakdown | null;
  paymentBreakdownLoading: boolean;
  paymentBreakdownError: string | null;
}

const initialState: DashboardSliceObject = {
  dashboardData: null,
  loading: false,
  error: null,
  selectedRange: 'Monthly',
  todayDashboardData: null,
  todayDashboardLoading: false,
  todayDashboardError: null,
  paymentBreakdown: null,
  paymentBreakdownLoading: false,
  paymentBreakdownError: null,
};

const dashboardSlice = createSlice({
  name: 'backofficeDashboard',
  initialState,
  reducers: {
    setDashboardLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setDashboardError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSelectedRange: (state, action: PayloadAction<DashboardRange>) => {
      state.selectedRange = action.payload;
    },
    setDashboardData: (state, action: PayloadAction<DashboardTotals>) => {
      state.dashboardData = action.payload;
    },
    setTodayDashboardData: (
      state,
      action: PayloadAction<DashboardTotals | null>,
    ) => {
      state.todayDashboardData = action.payload;
    },
    setTodayDashboardLoading: (state, action: PayloadAction<boolean>) => {
      state.todayDashboardLoading = action.payload;
    },
    setTodayDashboardError: (state, action: PayloadAction<string | null>) => {
      state.todayDashboardError = action.payload;
    },
    setPaymentBreakdown: (
      state,
      action: PayloadAction<PaymentMethodBreakdown | null>,
    ) => {
      state.paymentBreakdown = action.payload;
    },
    setPaymentBreakdownLoading: (state, action: PayloadAction<boolean>) => {
      state.paymentBreakdownLoading = action.payload;
    },
    setPaymentBreakdownError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.paymentBreakdownError = action.payload;
    },
  },
});

export const {
  setDashboardLoading,
  setDashboardError,
  setSelectedRange,
  setDashboardData,
  setTodayDashboardData,
  setTodayDashboardLoading,
  setTodayDashboardError,
  setPaymentBreakdown,
  setPaymentBreakdownLoading,
  setPaymentBreakdownError,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
